import { chunk, compact } from 'lodash-es';
import { RevenueOpportunityDismissalReason, RevenueOpportunityDismissalScope } from 'anchor-scout-common/types';
import { leadOpportunity, matchCard } from '~/utils/revenueOpportunities/opportunityCard';
import { kindText } from '~/utils/revenueOpportunities/opportunityLabels';
export const SINGLE_CARD_DISMISS_OPTIONS = [
    'already_handled',
    'not_relevant_for_client',
    'hide_kind',
    'numbers_look_off'
];
const GROUP_CARD_DISMISS_OPTIONS = [
    'not_relevant_for_service',
    'hide_kind',
    'numbers_look_off'
];
const MAX_DISMISSALS_PER_BATCH = 500;
const OPPORTUNITY_REASON_BY_OPTION = {
    already_handled: RevenueOpportunityDismissalReason.AlreadyHandled,
    not_relevant_for_client: RevenueOpportunityDismissalReason.NotRelevantForClient,
    numbers_look_off: RevenueOpportunityDismissalReason.NumbersLookOff
};
export const dismissOptions = (card)=>matchCard(card, {
        single: ()=>SINGLE_CARD_DISMISS_OPTIONS,
        group: ()=>GROUP_CARD_DISMISS_OPTIONS
    });
const singleDismissal = (body)=>({
        type: 'single',
        body
    });
const opportunityDismissal = (opportunity, reason)=>singleDismissal({
        scope: RevenueOpportunityDismissalScope.Opportunity,
        reason,
        opportunityKey: opportunity.opportunityKey
    });
const numbersLookOffBatches = (opportunities)=>chunk(opportunities.map((param)=>{
        let { opportunityKey } = param;
        return opportunityKey;
    }), MAX_DISMISSALS_PER_BATCH).map((opportunityKeys)=>({
            type: 'batch',
            body: {
                reason: RevenueOpportunityDismissalReason.NumbersLookOff,
                opportunityKeys
            }
        }));
export const dismissalRequests = (card, option)=>{
    const reason = OPPORTUNITY_REASON_BY_OPTION[option];
    if (option === 'hide_kind') {
        return [
            singleDismissal({
                scope: RevenueOpportunityDismissalScope.Kind,
                reason: RevenueOpportunityDismissalReason.KindTurnedOff,
                kind: leadOpportunity(card).kind
            })
        ];
    }
    return matchCard(card, {
        single: (param)=>{
            let { opportunity } = param;
            return reason ? [
                opportunityDismissal(opportunity, reason)
            ] : [];
        },
        group: (group)=>option === 'not_relevant_for_service' ? [
                singleDismissal({
                    scope: RevenueOpportunityDismissalScope.KindService,
                    reason: RevenueOpportunityDismissalReason.NotRelevantForService,
                    kind: group.kind,
                    serviceKey: group.serviceKey,
                    serviceLabel: group.subject
                })
            ] : numbersLookOffBatches(group.opportunities)
    });
};
export const matchDismissalRequest = (request, visitor)=>request.type === 'single' ? visitor.single(request.body) : visitor.batch(request.body);
export const matchDismissalUndo = (undo, visitor)=>undo.type === 'single' ? visitor.single(undo.dismissalId) : visitor.batch(undo.body);
export const undoOfSingleDismissal = (param)=>{
    let { id, created } = param;
    return created ? {
        type: 'single',
        dismissalId: id
    } : null;
};
export const undoOfBatchDismissal = (createdIds)=>createdIds.length > 0 ? {
        type: 'batch',
        body: {
            ids: createdIds
        }
    } : null;
const KEEPS_PARTIAL_WRITES = {
    single: ()=>false,
    batch: ()=>true
};
const mayHaveTakenEffect = (keepsPartialWrites, result)=>result.status === 'fulfilled' || keepsPartialWrites;
export const settleDismissals = (requests, results)=>{
    const dismissed = results.flatMap((result)=>result.status === 'fulfilled' ? [
            result.value
        ] : []);
    return {
        undos: compact(dismissed),
        succeededRequests: dismissed.length,
        failedRequests: results.length - dismissed.length,
        mayHaveChanged: requests.some((request, index)=>mayHaveTakenEffect(matchDismissalRequest(request, KEEPS_PARTIAL_WRITES), results[index]))
    };
};
export const settleUndos = (undos, results)=>({
        failedRequests: results.filter((param)=>{
            let { status } = param;
            return status === 'rejected';
        }).length,
        mayHaveChanged: undos.some((undo, index)=>mayHaveTakenEffect(matchDismissalUndo(undo, KEEPS_PARTIAL_WRITES), results[index]))
    });
const dismissOptionLabel = (option, kind, texts)=>{
    const menu = texts.section.dismissMenu;
    switch(option){
        case 'already_handled':
            return menu.alreadyHandled();
        case 'not_relevant_for_client':
            return menu.notRelevantForClient();
        case 'not_relevant_for_service':
            return menu.notRelevantForService();
        case 'hide_kind':
            return kindText(kind, menu.hideKind);
        case 'numbers_look_off':
            return menu.numbersLookOff();
    }
};
export const dismissChoices = (card, texts)=>dismissOptions(card).map((option)=>({
            option,
            label: dismissOptionLabel(option, leadOpportunity(card).kind, texts),
            biName: "pulse_opportunities_dismiss_".concat(option)
        }));
export const dismissedToastText = (option, texts)=>option === 'hide_kind' || option === 'not_relevant_for_service' ? texts.section.toasts.groupHidden() : texts.section.toasts.suggestionRemoved();
